import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { CheckboxControl, LabeledField } from '../../components/Controls'
import { useEscClose } from '../../components/useEscClose'
import SaberControlBar, { SABER_TEAL_ACCENT } from '../../components/SaberControlBar'
import type { PortfolioCompletenessProperty } from '../../saber.types'
import { resolvePropertyDisplay } from '../detail/labels'
import {
  buildDashboardPeriodOptions,
  resolveDashboardPeriod,
  type DateRange
} from './dashboardPeriod'
import { exportShadowBilling } from './exportShadowBilling'
import { date } from '../../format'
import classnames from 'classnames'

// Dashboard download slide-over (WEB-7069). Period is a seed copy of the
// Dashboard's current window so picking an export range does not refetch the
// portfolio query. Property list freezes on the first non-empty payload so a
// header period change cannot reshuffle the checklist, but opening during the
// portfolio fetch still picks up the arriving rows. Closes via Escape,
// backdrop, and Close/Cancel.

type Scope = 'portfolio' | 'selected'

export type DownloadProperty = Pick<PortfolioCompletenessProperty, 'propertyCuid' | 'propertyName' | 'location'>

export interface DownloadDrawerProps {
  properties: DownloadProperty[]
  seedPeriod: string
  seedCustomRange: DateRange | null
  onClose: () => void
}

const SECONDARY_BUTTON =
  'rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 ' +
  'dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700'

const PRIMARY_BUTTON =
  'rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 ' +
  'disabled:cursor-not-allowed disabled:opacity-50'

const SEARCH_INPUT =
  'w-full rounded-md border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ' +
  'dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500'

export const DownloadDrawer: React.FC<DownloadDrawerProps> = ({
  properties,
  seedPeriod,
  seedCustomRange,
  onClose
}) => {
  const [scope, setScope] = useState<Scope>('portfolio')
  const [selectedCuids, setSelectedCuids] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [period, setPeriod] = useState(seedPeriod)
  const [customRange, setCustomRange] = useState<DateRange | null>(seedCustomRange)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [propertySnapshot, setPropertySnapshot] = useState<DownloadProperty[] | null>(
    properties.length > 0 ? properties : null
  )
  if (propertySnapshot == null && properties.length > 0) {
    setPropertySnapshot(properties)
  }
  const listedProperties = propertySnapshot ?? properties
  const inFlightRef = useRef(false)
  const abortRef = useRef<AbortController | null>(null)

  useEscClose(true, onClose)

  useEffect(
    () => () => {
      abortRef.current?.abort()
    },
    []
  )

  const today = useMemo(() => new Date(), [])
  const periodOptions = useMemo(() => buildDashboardPeriodOptions(today), [today])
  const resolved = useMemo(() => resolveDashboardPeriod(period, customRange), [period, customRange])

  const onPeriodChange = (key: string): void => {
    if (key === 'custom') {
      if (customRange == null && resolved != null) {
        setPeriod('custom')
        setCustomRange(resolved)
      } else {
        setPeriod('custom')
      }
      return
    }
    // Leaving custom must drop the prior range. Otherwise focusing the picker
    // again keeps period=custom with the stale window (Dashboard clears URL from/to).
    setPeriod(key)
    setCustomRange(null)
  }

  const sorted = useMemo(() => {
    return [...listedProperties].sort((a, b) => {
      const an = resolvePropertyDisplay(a.propertyName, a.location).name
      const bn = resolvePropertyDisplay(b.propertyName, b.location).name
      return an.localeCompare(bn)
    })
  }, [listedProperties])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (q === '') return sorted
    return sorted.filter((p) => {
      const { name, location } = resolvePropertyDisplay(p.propertyName, p.location)
      return name.toLowerCase().includes(q) || (location?.toLowerCase().includes(q) ?? false)
    })
  }, [sorted, search])

  const canDownload =
    resolved != null && !loading && (scope === 'portfolio' || selectedCuids.length > 0)

  const toggleCuid = (cuid: string, checked: boolean): void => {
    setSelectedCuids((prev) => (checked ? [...prev, cuid] : prev.filter((c) => c !== cuid)))
  }

  const onDownload = (): void => {
    if (resolved == null || !canDownload || inFlightRef.current) return
    inFlightRef.current = true
    setLoading(true)
    setError(null)
    abortRef.current?.abort()
    const ac = new AbortController()
    abortRef.current = ac
    const args =
      scope === 'selected'
        ? { from: resolved.from, to: resolved.to, propertyCuids: selectedCuids, signal: ac.signal }
        : { from: resolved.from, to: resolved.to, signal: ac.signal }
    exportShadowBilling(args)
      .catch((err: unknown) => {
        if (ac.signal.aborted) return
        const message = err instanceof Error && err.message !== '' ? err.message : 'Download failed. Try again.'
        setError(message)
      })
      .finally(() => {
        inFlightRef.current = false
        if (!ac.signal.aborted) setLoading(false)
      })
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Download shadow billing"
    >
      <button type="button" aria-label="Close" className="flex-1 bg-black/40" onClick={onClose} />
      <div className="flex h-screen w-full max-w-2xl shrink-0 flex-col bg-white shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Download shadow billing</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">Measured meter-day extract as .xlsx</p>
          </div>
          <button type="button" onClick={onClose} className={SECONDARY_BUTTON}>
            Close
          </button>
        </div>

        <div data-testid="download-drawer-scroll" className="flex-1 space-y-5 overflow-y-auto p-4">
          <fieldset>
            <legend className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Scope
            </legend>
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
              Dashboard table filters do not change this export.
            </p>
            <div className="mt-2 space-y-2">
              <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="radio"
                  name="saber-download-scope"
                  value="portfolio"
                  checked={scope === 'portfolio'}
                  onChange={() => setScope('portfolio')}
                  className="mt-0.5 h-4 w-4 border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600"
                />
                <span>
                  Portfolio-wide
                  <span className="block text-xs text-slate-400 dark:text-slate-500">Every property SABER knows about</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="radio"
                  name="saber-download-scope"
                  value="selected"
                  checked={scope === 'selected'}
                  onChange={() => setScope('selected')}
                  className="mt-0.5 h-4 w-4 border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600"
                />
                <span>
                  Selected properties
                  <span className="block text-xs text-slate-400 dark:text-slate-500">
                    Choose one or more (names may not be unique)
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          {scope === 'selected' && (
            <div className="space-y-2">
              <div className="relative">
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  aria-hidden
                />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search properties"
                  aria-label="Search properties"
                  className={SEARCH_INPUT}
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
                  onClick={() => setSelectedCuids(visible.map((p) => p.propertyCuid))}
                >
                  Select all
                </button>
                <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                  ·
                </span>
                <button
                  type="button"
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
                  onClick={() => setSelectedCuids([])}
                >
                  Clear
                </button>
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  {selectedCuids.length} selected
                </span>
              </div>
              <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2 dark:border-slate-700">
                {visible.length === 0 ? (
                  <p className="px-1 py-2 text-xs text-slate-400 dark:text-slate-500">No properties match.</p>
                ) : (
                  visible.map((p) => {
                    const { name, location } = resolvePropertyDisplay(p.propertyName, p.location)
                    return (
                      <CheckboxControl
                        key={p.propertyCuid}
                        id={`download-prop-${p.propertyCuid}`}
                        checked={selectedCuids.includes(p.propertyCuid)}
                        onChange={(checked) => toggleCuid(p.propertyCuid, checked)}
                        className="w-full"
                        label={
                          <span className="flex min-w-0 flex-col">
                            <span>{name}</span>
                            {location != null && location !== '' && (
                              <span className="text-xs font-normal text-slate-400 dark:text-slate-500">{location}</span>
                            )}
                          </span>
                        }
                      />
                    )
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Period sits outside overflow-y-auto so the datepicker popover is not clipped. */}
        <div className="shrink-0 overflow-visible border-t border-slate-200 px-4 pt-3 dark:border-slate-700">
          <LabeledField label="Period">
            {resolved != null && (
              <span className={classnames('inline-flex items-center gap-1 text-sm font-medium', SABER_TEAL_ACCENT)}>
                {date(resolved.from)} – {date(resolved.to)}
              </span>
            )}
            <SaberControlBar
              periods={periodOptions}
              period={period}
              onPeriodChange={onPeriodChange}
              customRange={customRange}
              onCustomRangeChange={setCustomRange}
              customRangeTestId="download-custom-range-picker"
              popoverDirection="up"
            />
          </LabeledField>
        </div>

        <div className="space-y-3 px-4 py-3">
          {error != null && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className={SECONDARY_BUTTON}>
              Cancel
            </button>
            <button type="button" onClick={onDownload} disabled={!canDownload} className={PRIMARY_BUTTON}>
              {loading ? 'Downloading…' : 'Download'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default DownloadDrawer
